/**
 * UserCmd.js
 * Classes to encapsulate user actions in BMaps
 *
 * The following concepts are defined:
 * UserCmd: is a possible kind of action, but without specific arguments (analogous to a class)
 * UserAction: is an the "instantiation" of the UserCmd, with arguments
 * UserActions is an object containing functions for to run all the UserCmds. (dynamically built)
 *
 * Normal use is to import UserActions from UserAction.js and call the functions on it.
 *
 * Action Queue:
 * Actions are put into a queue to hopefully allow for command undo/redo.
 * EventBroker "zapAll" is subscribed, so "ActionLog" and "UndoQueue" can be cleared on zap,
 * along with every snapshot the undoable commands have recorded (see ./undoable.js).
 *
 * Undo:
 * A command is undoable in one of two ways. The older is an `undoFn` option, which receives the
 * command's own arguments -- enough when the inverse can be computed from them (no command on
 * main uses it now). The other is the capture / apply / restore triple: `capture(...args)`
 * remembers what is about to change, `apply(...args)` is the command body, and
 * `restore(snapshot)` puts it back. ./undoable.js composes those into the doFn/undoFn pair and
 * owns where the snapshot lives between do and undo.
 */

import { EventBroker } from '../eventbroker';
import { undoable, resetAll } from './undoable';

/**
 * Returned by undo/redo when there was nothing on the stack to act on.
 *
 * Module-private and compared by IDENTITY, so nothing a real command returns can be mistaken
 * for it. Both used to return `undefined` whether they had undone something or found an empty
 * stack, which left the two indistinguishable -- so `undo` on an empty stack reported
 * "Done: Undo" with the state byte-identical (BUG-02 of the 2026-09-21 narration audit).
 */
const NOTHING_TO_DO = Object.freeze({ nothingToDo: true });

// A UserCmd is a potential user action.
// It contains the logic, but not the arguments.
export class UserCmd {
    constructor(name, doFn, options={}) {
        this.name = name;
        this.redoFn = options.redoFn;
        // "Running me changes nothing" -- a pure read, or undo/redo itself. Distinct from having
        // no undoFn, which means "I changed something you cannot get back". See Enqueue.
        this.noEffect = options.noEffect;
        // "This particular call changed nothing", decided from the RESULT. Distinct from
        // `noEffect`, which is a property of the command itself. Narration reads it so a call
        // that did nothing is not reported as done (gibbs/ui/describeExecution).
        this.isNoOp = options.isNoOp;

        const { capture, restore, undoFn } = options;
        if (capture || restore) {
            if (undoFn) {
                throw new TypeError(`UserCmd ${name}: declare capture/restore or undoFn, not both.`);
            }
            const composed = undoable({
                name, capture, apply: doFn, restore,
            });
            // Kept addressable on the command, not only inside the composed closures, so the
            // snapshot's home can move into the queued UserAction without touching a command.
            this.capture = capture;
            this.apply = doFn;
            this.restore = restore;
            this.doFn = composed.doFn;
            this.undoFn = composed.undoFn;
        } else {
            this.doFn = doFn;
            this.undoFn = undoFn;
        }
    }

    do(...args) {
        return UserAction.do(this, ...args);
    }
}

// A UserAction is something the user has done.
// It is a UserCmd (a potential action) plus arguments.
// A UserAction can theoretically be undone and redone.
export class UserAction {
    static do(cmd, ...args) {
        return new UserAction(cmd, ...args).do();
    }

    static undo() {
        if (!UserAction.canUndo()) return NOTHING_TO_DO;
        return UserAction.PrevAction.undo();
    }

    static redo() {
        if (!UserAction.canRedo()) return NOTHING_TO_DO;
        return UserAction.NextAction.redo();
    }

    static canUndo() {
        return UserAction.PrevAction && UserAction.PrevAction.cmd.undoFn;
    }

    static canRedo() {
        return UserAction.NextAction;
    }

    constructor(cmd, ...args) {
        this.cmd = cmd;
        this.args = args;
    }

    do() {
        const cmd = this.cmd;
        const result = cmd.doFn(...this.args);
        UserAction.Enqueue(this);
        return result;
    }

    undo() {
        const cmd = this.cmd;
        if (cmd.undoFn) {
            UserAction.UndoIndex--;
            return cmd.undoFn(...this.args);
        } else {
            console.warn('Attempted to undo un-undoable action.');
            return undefined;
        }
    }

    redo() {
        const cmd = this.cmd;
        // Not exactly sure how redo will be used, but this allows a custom "redo" function,
        // or fallback to "do" function
        const verb = cmd.redoFn ? cmd.redoFn : cmd.doFn;
        if (UserAction.UndoQueue[UserAction.UndoIndex] === this) {
            UserAction.UndoIndex++;
            try {
                verb(...this.args);
            } catch (error) {
                // A redo that throws did not happen: leave it redoable, and keep the index in
                // step with the undoable seam, which records nothing for a call that threw.
                UserAction.UndoIndex--;
                throw error;
            }
        } else {
            console.warn('Attempted to redo the wrong action.');
        }
    }

    // Action queue managment
    static setupUndoQueue() {
        if (!UserAction.undoQueue) {
            UserAction.undoQueue = [];
            UserAction.undoIndex = 0;
        }
    }

    static get ActionLog() {
        if (!UserAction.actionLog) {
            UserAction.actionLog = [];
            UserAction.zapHandler = EventBroker.subscribe('zapAll', () => UserAction.ResetQueues());
        }
        return UserAction.actionLog;
    }

    static get UndoQueue() {
        UserAction.setupUndoQueue();
        return UserAction.undoQueue;
    }

    static get UndoIndex() {
        UserAction.setupUndoQueue();
        return UserAction.undoIndex;
    }

    static set UndoIndex(index) {
        UserAction.setupUndoQueue();
        UserAction.undoIndex = index;
    }

    static get PrevAction() {
        UserAction.setupUndoQueue();
        return UserAction.UndoQueue[UserAction.UndoIndex-1];
    }

    static get NextAction() {
        UserAction.setupUndoQueue();
        return UserAction.UndoQueue[UserAction.UndoIndex];
    }

    static Enqueue(action) {
        UserAction.ActionLog.push(action);
        // Two separate questions, and conflating them is what the old `skipQueue` did:
        //   1. Did this command change anything?  If not, leave the queue untouched.
        //   2. Can it be undone?  Only then is it worth recording.
        // A command that changes state but has no undoFn answers yes/no -- it must still discard
        // the redo branch, because whatever sits ahead of UndoIndex describes a state that no
        // longer exists. Losing that was a regression in the first cut of BP2-2723.
        if (!action.cmd.noEffect) {
            UserAction.UndoQueue.splice(UserAction.UndoIndex);
            if (action.cmd.undoFn) {
                UserAction.UndoQueue.push(action);
                UserAction.UndoIndex++;
            }
        }
    }

    static ResetQueues() {
        UserAction.actionLog = [];
        UserAction.undoQueue = [];
        UserAction.undoIndex = 0;
        resetAll();
    }

    static DumpQueues() {
        const dumpOneQueue = (q, active) => q.map((a, i) => `${active===i?'*':''}${a.cmd.name}`).join('\n');
        console.log(`Action log: ${dumpOneQueue(UserAction.ActionLog)}`);
        console.log(`Undo queue: ${dumpOneQueue(UserAction.UndoQueue, UserAction.UndoIndex)}`);
    }
}

// CmdRegistry is an object with functions that will execute
// every possible UserCmd
export const UserActions = {

};

export const CustomActions = {
    Compound: [],
    // CompoundGroup: [],
    // CompoundView: [],
    // Fragment: [],
    // FragmentGroup: [],
    // FragmentView: [],
    // ProteinView: [],
    // HotspotGroup: [],
};

export function AddUserActions(cmdObj, childKey) {
    const host = childKey ? UserActions[childKey] : UserActions;
    for (const [cmdName, cmdFn] of Object.entries(cmdObj)) {
        if (host[cmdName]) console.warn(`${cmdName} is already defined as a user action${childKey ? `on ${childKey}` : ''}. Overriding...`);
        const wrapper = (...args) => UserAction.do(cmdFn, ...args);
        // The UserCmd itself, reachable from its registered name. Without this a caller holding
        // `UserActions.X` could see only an arrow function: not the real callee, not its arity,
        // not its `isNoOp`. That made two things impossible -- a mechanical check that an
        // ActionDef's declaration fits the function it dispatches to, and the no-op detection
        // describeExecution was written for -- so both were carried as prose audits and as
        // permanently-failing tests instead.
        wrapper.cmd = cmdFn;
        host[cmdName] = wrapper;
    }
}

export function AddCustomAction(type, actions) {
    for (const action of [].concat(actions)) {
        CustomActions[type].push(action);
    }
}

// noEffect, not merely "no undoFn": these two WALK the queue, so they must not disturb it.
// Without it, Undo would splice away the very redo branch it had just created.
const UndoActions = {
    Undo: new UserCmd('Undo', UserAction.undo, {
        noEffect: true,
        isNoOp: (result) => result === NOTHING_TO_DO,
    }),
    Redo: new UserCmd('Redo', UserAction.redo, {
        noEffect: true,
        isNoOp: (result) => result === NOTHING_TO_DO,
    }),
};

AddUserActions(UndoActions);
